import {
  setOrderParcelMutation,
  updateOrderNoteMutation,
  updateOrderStateMutation,
} from '@/api/orders/orderMutations';
import { getOrderQuery } from '@/api/orders/orderQueries';
import { createParcelMutation } from '@/api/parcels/parcelMutations';
import { fetchParcelSticker } from '@/api/parcels/parcelQueries';
import { queryKeys } from '@/api/queryKeys';
import { Button } from '@/components/common/Button';
import { Dialog, DialogContent } from '@/components/common/Dialog';
import { Input } from '@/components/common/Input';
import { Textarea } from '@/components/common/Textarea';
import type { Order } from '@/domain/Order';
import { Role } from '@/domain/Role';
import { useFormMutation } from '@/hooks/useFormMutation';
import { useHasRole } from '@/hooks/useHasRole';
import { cn } from '@/lib/cn';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import UserCard from '../orders-common/UserCard';
import type { SupplementFormData } from './SupplementFactsForm';
import { SupplementFactsForm } from './SupplementFactsForm';
import { SupplementFactsFormOld } from './SupplementFactsFormOld';
import { SupplementFactsPreview } from './SupplementFactsPreview';
import { SupplementFactsPreviewOld } from './SupplementFactsPreviewOld';

const emptySupplementFormData = (): SupplementFormData => ({
  name: '',
  selectedSupplements: [],
  instruction: '',
  textarea: '',
  quantities: {},
  types: {},
});

interface OrderDetailsProps {
  order: Order;
}

export function OrderDetails({ order: orderProp }: OrderDetailsProps) {
  const queryClient = useQueryClient();
  const { hasRole } = useHasRole();
  const isAdmin = hasRole(Role.SHOP_ADMIN, Role.SUPERADMIN);
  const canManageParcel = hasRole(
    Role.SHOP_ADMIN,
    Role.SUPERADMIN,
    Role.PACKER,
  );
  const { data: orderResponse } = useQuery(getOrderQuery(orderProp.id));
  const order = orderResponse?.data ?? orderProp;
  const updateState = useFormMutation(updateOrderStateMutation());
  const createParcel = useMutation(createParcelMutation());
  const saveNote = useFormMutation(updateOrderNoteMutation());
  const saveParcel = useFormMutation(setOrderParcelMutation());
  const [showSupplementFacts, setShowSupplementFacts] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showSupplementFactsOld, setShowSupplementFactsOld] = useState(false);
  const [showPreviewOld, setShowPreviewOld] = useState(false);
  const [isStickerLoading, setIsStickerLoading] = useState(false);
  const [note, setNote] = useState(order.note ?? '');
  const [sendAll, setSendAll] = useState(false);
  const [parcelTrack, setParcelTrack] = useState(order.parcel_track ?? '');

  // An order without a shipping address can't have a parcel auto-created.
  const hasAddress = 'id' in order.address;

  useEffect(() => {
    setNote(order.note ?? '');
  }, [order.id, order.note]);

  // Prefill the tracking number with the order's current parcel_track; still
  // editable so an admin can correct it.
  useEffect(() => {
    setParcelTrack(order.parcel_track ?? '');
  }, [order.id, order.parcel_track]);

  const [formData, setFormData] = useState<SupplementFormData>(
    emptySupplementFormData,
  );
  const [formDataOld, setFormDataOld] = useState<SupplementFormData>(
    emptySupplementFormData,
  );

  const handleFormDataChange = (data: Partial<SupplementFormData>) => {
    setFormData((prev) => ({ ...prev, ...data }));
  };

  const handleFormDataOldChange = (data: Partial<SupplementFormData>) => {
    setFormDataOld((prev) => ({ ...prev, ...data }));
  };

  const mapSupplements = (data: SupplementFormData) =>
    data.selectedSupplements
      .filter((index) => order.items[index])
      .map((index) => ({
        item: order.items[index],
        quantity:
          data.quantities[index] ||
          order.items[index].quantity ||
          order.items[index].pills ||
          1,
        type: data.types[index] || 'capsule',
      }));

  const handleApprove = async () => {
    await updateState.mutateAsync({
      orderId: order.id,
      state: 'Approved',
      send_all: sendAll,
    });
    // Skip parcel auto-creation when there's no address to ship to — the parcel
    // can be added manually below instead.
    if (hasAddress) {
      await createParcel.mutateAsync(order.id);
    }
    await queryClient.invalidateQueries({ queryKey: queryKeys.orders.all() });
    await queryClient.invalidateQueries({
      queryKey: queryKeys.orders.order(order.id),
    });
  };

  const handleSaveParcel = async () => {
    const trackingNumber = parcelTrack.trim();
    if (!trackingNumber) return;
    await saveParcel.mutateAsync({
      orderId: order.id,
      parcel_track: trackingNumber,
    });
    await queryClient.invalidateQueries({ queryKey: queryKeys.orders.all() });
    await queryClient.invalidateQueries({
      queryKey: queryKeys.orders.order(order.id),
    });
  };

  const handleReject = async () => {
    await updateState.mutateAsync({ orderId: order.id, state: 'Rejected' });
    queryClient.invalidateQueries({ queryKey: queryKeys.orders.all() });
  };

  const handleSaveNote = async () => {
    await saveNote.mutateAsync({ orderId: order.id, note });
    queryClient.invalidateQueries({ queryKey: queryKeys.orders.all() });
    queryClient.invalidateQueries({
      queryKey: queryKeys.orders.order(order.id),
    });
  };

  const handleShipping = async () => {
    setIsStickerLoading(true);
    try {
      const blob = await fetchParcelSticker(order.id);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : 'Failed to load shipping sticker',
      );
    } finally {
      setIsStickerLoading(false);
    }
  };

  return (
    <div className="space-y-6 pt-4">
      <div className="flex flex-col gap-6">
        <p className="text-table-foreground text-lg font-medium">
          Order ID: {order.id}
        </p>

        <div className="flex gap-4">
          <Button
            variant="outlinePrimary"
            className="hover:bg-primary h-10 rounded-lg px-6 text-base hover:text-white"
            onClick={handleApprove}
            disabled={
              order.state === 'Approved' ||
              updateState.isPending ||
              createParcel.isPending
            }
          >
            Approve
          </Button>
          <Button
            variant="outlinePrimary"
            className="hover:bg-primary h-10 rounded-lg px-6 text-base hover:text-white"
            onClick={handleReject}
            disabled={updateState.isPending}
          >
            Reject
          </Button>
        </div>
        {hasRole(Role.SHOP_ADMIN, Role.SUPERADMIN, Role.PACKER) && (
          <div
            className={cn(
              'flex items-center gap-2.5 text-sm select-none',
              order.state === 'Approved' ||
                updateState.isPending ||
                createParcel.isPending
                ? 'opacity-50'
                : '',
            )}
          >
            <label className="group relative flex shrink-0 cursor-pointer items-center">
              <input
                type="checkbox"
                checked={sendAll}
                onChange={(e) => setSendAll(e.target.checked)}
                disabled={
                  order.state === 'Approved' ||
                  updateState.isPending ||
                  createParcel.isPending
                }
                className="peer absolute h-0 w-0 opacity-0"
              />
              <span className="border-muted-foreground group-hover:bg-muted/20 peer-checked:bg-primary relative h-5 w-5 rounded-sm border-2 transition-colors peer-disabled:cursor-not-allowed after:absolute after:top-px after:left-1.25 after:hidden after:h-3 after:w-1.5 after:rotate-45 after:border-r-2 after:border-b-2 after:border-white after:content-[''] peer-checked:after:block" />
            </label>
            <span>Send as whole</span>
          </div>
        )}
      </div>
      <div className="space-y-2">
        <Textarea
          id={`order-note-${order.id}`}
          label="Note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          disabled={!isAdmin || saveNote.isPending}
          rows={3}
        />
        {isAdmin && (
          <div className="flex justify-end">
            <Button
              variant="outlinePrimary"
              className="hover:bg-primary h-10 rounded-lg px-6 text-base hover:text-white"
              onClick={handleSaveNote}
              disabled={saveNote.isPending || note === (order.note ?? '')}
            >
              {saveNote.isPending ? 'Saving...' : 'Save'}
            </Button>
          </div>
        )}
      </div>
      {canManageParcel && (
        <div className="space-y-2">
          <p className="text-table-foreground mb-4 text-sm font-medium">
            Manual parcel
          </p>
          <Input
            id={`parcel-track-${order.id}`}
            label="Tracking number"
            value={parcelTrack}
            onChange={(e) => setParcelTrack(e.target.value)}
            disabled={saveParcel.isPending}
          />
          <div className="flex justify-end">
            <Button
              variant="outlinePrimary"
              className="hover:bg-primary h-10 rounded-lg px-6 text-base hover:text-white"
              onClick={handleSaveParcel}
              disabled={
                !parcelTrack.trim() ||
                parcelTrack.trim() === (order.parcel_track ?? '') ||
                saveParcel.isPending
              }
            >
              {saveParcel.isPending ? 'Saving...' : 'Save parcel'}
            </Button>
          </div>
        </div>
      )}

      <UserCard order={order} />
      <div className="flex gap-4">
        <Button
          variant="outlinePrimary"
          className="h-10 rounded-lg px-6 text-base"
          onClick={handleShipping}
          disabled={order.parcel_id == null || isStickerLoading}
        >
          {isStickerLoading ? 'Loading...' : 'Shipping'}
        </Button>
        <Button
          variant="outlinePrimary"
          className="h-10 rounded-lg px-6 text-base"
          onClick={() => setShowSupplementFacts(true)}
        >
          Supplement facts
        </Button>
        <Button
          variant="outlinePrimary"
          className="h-10 rounded-lg px-6 text-base"
          onClick={() => setShowSupplementFactsOld(true)}
        >
          Old
        </Button>
      </div>
      <Dialog
        open={showSupplementFacts}
        onOpenChange={(open) => {
          setShowSupplementFacts(open);
          if (!open) setShowPreview(false);
        }}
      >
        <DialogContent
          className={cn(
            'border-none p-0',
            showPreview
              ? 'max-h-[95vh] max-w-[920px] overflow-y-auto rounded-none bg-neutral-300'
              : 'bg-form-linear max-w-lg rounded-xl p-6',
          )}
          showCloseButton={false}
        >
          {showPreview ? (
            <SupplementFactsPreview
              orderId={order.id}
              name={formData.name}
              supplements={mapSupplements(formData)}
              instruction={formData.textarea}
              onBack={() => setShowPreview(false)}
            />
          ) : (
            <SupplementFactsForm
              order={order}
              formData={formData}
              onFormDataChange={handleFormDataChange}
              onView={() => setShowPreview(true)}
              onExit={() => setShowSupplementFacts(false)}
            />
          )}
        </DialogContent>
      </Dialog>
      <Dialog
        open={showSupplementFactsOld}
        onOpenChange={(open) => {
          setShowSupplementFactsOld(open);
          if (!open) setShowPreviewOld(false);
        }}
      >
        <DialogContent
          className={cn(
            'max-w-lg border-none p-0',
            showPreviewOld
              ? 'rounded-none bg-white'
              : 'bg-form-linear rounded-xl p-6',
          )}
          showCloseButton={false}
        >
          {showPreviewOld ? (
            <SupplementFactsPreviewOld
              orderId={order.id}
              name={formDataOld.name}
              supplements={mapSupplements(formDataOld)}
              instruction={formDataOld.textarea}
              onBack={() => setShowPreviewOld(false)}
            />
          ) : (
            <SupplementFactsFormOld
              order={order}
              formData={formDataOld}
              onFormDataChange={handleFormDataOldChange}
              onView={() => setShowPreviewOld(true)}
              onExit={() => setShowSupplementFactsOld(false)}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
